import { Controller } from "@hotwired/stimulus";

// LOC-101: the image set on the portfolio item edit page. Freshly uploaded
// images have no thumbnail yet (OptimizeProjectFileWorker renders the variants
// in the background), so their tiles show a spinner. While any tile is
// processing, the list is re-fetched from the files index every few seconds and
// swapped in place, so the thumbnails appear without a reload. A tile still
// processing after the timeout is shown as failed, so nothing spins forever.
export default class extends Controller {
  static targets = ["list"];
  static values = {
    url: String,
    failedLabel: String,
    interval: { type: Number, default: 3000 },
    timeout: { type: Number, default: 120000 },
  };

  connect() {
    this.startedAt = Date.now();
    this.schedule();
  }

  disconnect() {
    this.stop();
  }

  schedule() {
    this.stop();
    if (!this.processing()) return;

    this.timer = setTimeout(() => this.refresh(), this.intervalValue);
  }

  stop() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  processing() {
    return this.element.querySelector("[data-processing]") !== null;
  }

  async refresh() {
    if (Date.now() - this.startedAt > this.timeoutValue) {
      this.giveUp();
      return;
    }

    try {
      const response = await fetch(this.urlValue, { headers: { Accept: "text/html" }, credentials: "same-origin" });
      if (response.ok) this.listTarget.outerHTML = await response.text();
    } catch (_) {
      // Network hiccup: try again on the next tick.
    }
    this.schedule();
  }

  // The worker gave up or never ran: show the error state instead of a spinner.
  giveUp() {
    this.element.querySelectorAll("[data-processing]").forEach((tile) => {
      tile.replaceWith(this.failedTile());
    });
  }

  // Mirrors the failed tile in _image_tiles.
  failedTile() {
    const tile = document.createElement("span");
    tile.className = "locals-thumb-sm locals-thumb-state locals-thumb-state--failed";
    tile.setAttribute("role", "img");
    tile.setAttribute("title", this.failedLabelValue);
    tile.setAttribute("aria-label", this.failedLabelValue);
    const icon = document.createElement("i");
    icon.className = "bi bi-exclamation-triangle";
    icon.setAttribute("aria-hidden", "true");
    tile.appendChild(icon);
    return tile;
  }
}
